import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useCreateSponsorship } from "@/api/sponsorships";
import { useAuth } from "@/contexts/AuthContext";
import { useChapters } from "@/api/chapters";
import PropTypes from "prop-types";
import { useMemo } from "react";

const formSchema = z.object({
  sponsorName: z.string().min(2, { message: "Sponsor name is required" }),
  sponsorPhone: z.string().min(1, { message: "Phone is required" }),
  mahalluName: z.string().min(1, { message: "Mahallu name is required" }),
  amountPromised: z.string().min(1, { message: "Amount is required" }),
  currency: z.string().default("INR"),
  careOfName: z.string().min(2, { message: "Care of name is required" }),
  careOfPhone: z.string().regex(/^[+]?\d{1,15}$/, {
    message: "Please enter a valid phone number (maximum 15 digits)",
  }),
});

export function AddMahalluSponsorshipForm({ onSuccess }) {
  const { user } = useAuth();
  const { data: chapters } = useChapters();
  const { mutate: createSponsorship, isPending } = useCreateSponsorship();

  const myChapter = useMemo(
    () => (chapters || []).find((ch) => ch._id === user?.chapter),
    [chapters, user],
  );
  const mahalluOptions = myChapter?.mahalluList || [];

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      sponsorName: "",
      sponsorPhone: "",
      mahalluName: "",
      amountPromised: "",
      currency: "INR",
      careOfName: "",
      careOfPhone: "",
    },
  });

  function onSubmit(values) {
    createSponsorship(
      {
        type: "Mahallu",
        sponsorName: values.sponsorName,
        sponsorPhone: values.sponsorPhone || undefined,
        chapter: user?.chapter,
        mahalluName: values.mahalluName,
        amountPromised: parseInt(values.amountPromised, 10),
        currency: values.currency,
        careOf: {
          name: values.careOfName || undefined,
          phone: values.careOfPhone || undefined,
        },
      },
      {
        onSuccess: () => {
          form.reset();
          onSuccess?.();
        },
      },
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* Mahallu Select */}
        <FormField
          control={form.control}
          name="mahalluName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Mahallu <span className="text-rose-600 ml-1">*</span>
              </FormLabel>
              {mahalluOptions.length > 0 ? (
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select mahallu" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {mahalluOptions.map((name) => (
                      <SelectItem key={name} value={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <FormControl>
                  <Input placeholder="Enter mahallu name" {...field} />
                </FormControl>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Sponsor Details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="sponsorName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Sponsor Name <span className="text-rose-600 ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Enter name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="sponsorPhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Phone <span className="text-rose-600 ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Enter phone" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Amount */}
        <div className="grid grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="amountPromised"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Amount Promised <span className="text-rose-600 ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input type="number" placeholder="Amount" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Currency</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Currency" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="INR">₹ INR</SelectItem>
                    <SelectItem value="SAR">﷼ SAR</SelectItem>
                    <SelectItem value="QAR">ر.ق QAR</SelectItem>
                    <SelectItem value="AED">د.إ AED</SelectItem>
                    <SelectItem value="KWD">د.ك KWD</SelectItem>
                    <SelectItem value="OMR">ر.ع. OMR</SelectItem>
                    <SelectItem value="BHD">.د.ب BHD</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Care Of */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="careOfName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  C/O Name <span className="text-rose-600 ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Care of name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="careOfPhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  C/O Phone <span className="text-rose-600 ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Care of phone" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button type="submit" className="w-full" disabled={isPending}>
          {isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            "Add Mahallu Sponsorship"
          )}
        </Button>
      </form>
    </Form>
  );
}

AddMahalluSponsorshipForm.propTypes = {
  onSuccess: PropTypes.func,
};
